import {
  BottomSheet,
  CbhIcon,
  Divider,
  Pill,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { differenceInMinutes, isPast } from "date-fns";
import { useHistory } from "react-router-dom";

import { calculateMinutesElapsed } from "../../ArrivalTime/calculateMinutesElapsed";
import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { formatDurationWithBreakParts } from "../../utils/formatDurationWithBreakParts";
import { formatTimeRange } from "../../utils/formatTimeRange";
import { InstantPayIndicator } from "../../WorkerShifts/components/InstantPayIndicator";

export interface ConfirmBookingBottomSheetProps {
  readonly modalState: UseModalState;
  readonly shiftStart: Date;
  readonly shiftEnd: Date;
  readonly timezone: string;
  readonly mandatoryBreakDurationInMinutes: number;
  readonly requiresBreak: boolean;
  readonly urgentPillLabel: string | undefined;
  readonly workplaceName: string;
  readonly formattedDistance?: string;
  readonly hourlyPay?: string;
  readonly totalWithBoostPay?: string;
  readonly useShiftBoost: boolean;
  readonly shiftBoostAmount?: number;
  readonly isInstantPay: boolean;
  readonly isLoading: boolean;
  readonly isIndependentContractorBadgesRequired?: boolean;
  readonly onConfirmBooking: () => Promise<void>;
}

export function ConfirmBookingBottomSheet(props: ConfirmBookingBottomSheetProps) {
  const {
    modalState,
    shiftStart,
    shiftEnd,
    timezone,
    mandatoryBreakDurationInMinutes,
    requiresBreak,
    urgentPillLabel,
    workplaceName,
    formattedDistance,
    hourlyPay,
    totalWithBoostPay,
    useShiftBoost,
    shiftBoostAmount,
    isInstantPay,
    isIndependentContractorBadgesRequired = false,
    isLoading,
    onConfirmBooking,
  } = props;

  const history = useHistory();

  const dateDisplay = formatDayOfMonthWithDayName(shiftStart, { timezone });
  const timeDisplay = formatTimeRange(
    { startDate: shiftStart, endDate: shiftEnd },
    { timezone, useNowForStartDate: isPast(shiftStart) }
  );

  const totalDurationInHours = differenceInMinutes(shiftEnd, shiftStart) / 60;
  const minutesElapsed = calculateMinutesElapsed(shiftStart, new Date());

  const { duration, breakInfo } = formatDurationWithBreakParts({
    totalDurationInHours,
    breakDurationInMinutes: mandatoryBreakDurationInMinutes,
    requiresBreak,
    minutesElapsed,
  });

  return (
    <BottomSheet
      modalState={modalState}
      header={
        <BottomSheetHeader
          onBack={() => {
            history.goBack();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical" closeButtonAriaLabel="Close booking confirmation">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isLoading}
            onClick={onConfirmBooking}
          >
            Confirm shift
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent sx={{ backgroundColor: "unset", width: "100%", padding: 5 }}>
        <Stack direction="column" alignItems="flex-start" spacing={9}>
          <Title variant="h4" component="h4" sx={{ textAlign: "left", textWrap: "balance" }}>
            Confirm your shift details
          </Title>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              columnGap: 8,
              rowGap: 6,
              alignItems: "start",
            }}
          >
            <Text semibold variant="h6">
              When
            </Text>
            <Stack spacing={2}>
              <Text semibold variant="body1">
                {dateDisplay}
              </Text>
              <Text semibold variant="body1">
                {timeDisplay} ({duration})
              </Text>
              {breakInfo && (
                <Text variant="body1" color="text.secondary">
                  {breakInfo}
                </Text>
              )}
              {urgentPillLabel && (
                <Pill
                  size="small"
                  variant="filled"
                  label={urgentPillLabel}
                  iconType="clock-filled"
                  backgroundColor={(theme) => theme.palette.shift?.urgent.background}
                  color={(theme) => theme.palette.shift?.urgent.text}
                  iconColor={(theme) => theme.palette.shift?.urgent.icon}
                  sx={{ width: "fit-content" }}
                />
              )}
            </Stack>

            <Divider sx={{ gridColumn: "1 / -1" }} />

            <Text semibold variant="h6">
              Workplace
            </Text>
            <Stack spacing={2}>
              <Text semibold variant="body1">
                {workplaceName}
              </Text>
              {formattedDistance && (
                <Text variant="body1" color="text.secondary">
                  {formattedDistance} from home
                </Text>
              )}
            </Stack>

            <Divider sx={{ gridColumn: "1 / -1" }} />

            {isIndependentContractorBadgesRequired && (
              <>
                <Text semibold variant="h6" sx={{ whiteSpace: "pre-line" }}>
                  {"What to\nexpect"}
                </Text>
                <Stack direction="row" spacing={2} alignItems="flex-end">
                  <CbhIcon type="badge" size="small" />
                  <Text semibold variant="body1">
                    Badge Required
                  </Text>
                </Stack>

                <Divider sx={{ gridColumn: "1 / -1" }} />
              </>
            )}

            <Text semibold variant="h6">
              Your pay
            </Text>
            <Stack spacing={2}>
              <Text semibold variant="body1">
                {hourlyPay} /hr
              </Text>
              <Text variant="body1" color="text.secondary">
                {totalWithBoostPay} estimated total
              </Text>
              {useShiftBoost && isDefined(shiftBoostAmount) && (
                <Pill
                  size="small"
                  variant="filled"
                  label={`${formatDollarsAsUsd(shiftBoostAmount)} Shift Boost included`}
                  iconType="bolt-filled"
                  backgroundColor={(theme) => theme.palette.intent?.success.background}
                  color={(theme) => theme.palette.intent?.success.text}
                  iconColor={(theme) => theme.palette.intent?.success.text}
                  sx={{ width: "fit-content" }}
                />
              )}
              {isInstantPay && <InstantPayIndicator />}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
